import { Badge } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';
import { useTranslation } from 'react-i18next';

export type VerificationBadgeProps = {
  className?: string;
  /**
   * Whether the value is verified. `undefined` renders nothing: an absent requirement means the
   * policy never asked for this value, which is neither verified nor pending.
   */
  isVerified?: boolean;
};

/**
 * The state of one contact value, as the badge beside its field.
 *
 * It exists so the badges of this domain cannot drift apart: same colors, same shape, one place.
 *
 * @param {VerificationBadgeProps} props - Component properties
 */
export const VerificationBadge = ({ className, isVerified }: VerificationBadgeProps) => {
  const { t } = useTranslation('identity');

  if (isVerified === undefined) return null;

  return (
    <Badge
      className={cn(
        'w-fit font-sans text-xs font-semibold',
        {
          'border-success bg-bg-success text-success': isVerified,
          'border-destructive bg-destructive/10 text-destructive': !isVerified,
        },
        className,
      )}
      data-state={isVerified ? 'verified' : 'unverified'}
      data-testid="verification-badge"
      variant="outline"
    >
      {t(isVerified ? 'verificationBadge.verified' : 'verificationBadge.unverified')}
    </Badge>
  );
};
